import { openAPIClient } from '../openApiClient';
import type {
  CreateSequenceServiceConfig,
  DeleteSequenceServiceConfig,
  GetSequenceByIdServiceConfig,
  InstructionFeedbackServiceConfig,
  PatchSequenceServiceConfig,
  SequenceByIdResponse,
  SequenceChangelogResponse,
  SequenceChangelogServiceConfig,
  SequenceCreateResponse,
  SequenceInstructionFeedbackResponse,
  SequenceListResponse,
  SequenceListServiceConfig,
  SequencePatchResponse,
  SequenceSynthesizeSpeechResponse,
  SequenceTranslateResponse,
  SequenceUpdateResponse,
  SynthesizeSpeechServiceConfig,
  TranslateSequenceServiceConfig,
  UpdateSequenceServiceConfig,
} from './types/index';

export async function getSequenceListService({
  httpRequestConfig,
  params,
}: SequenceListServiceConfig): Promise<SequenceListResponse> {
  const client = await openAPIClient(httpRequestConfig);

  if (params === undefined) {
    throw new Error('Params are required');
  }

  const response = await client.GET('/v1/auth/sequence/{spaceId}/{sequenceLanguage}', {
    params: {
      path: {
        spaceId: httpRequestConfig.space,
        sequenceLanguage: params.query.language,
      },
      query: {
        ...params.query,
        // because of SequenceListQueryParams type, we need to cast the filters to a string while it's an object in reality and rely on the openapi client to convert it to a string
        filters: params.query.filters as string,
      },
    },
  });

  if (response.error !== undefined) {
    throw new Error(response.error.message);
  }

  return response.data;
}

export async function getSequenceByIdService({
  httpRequestConfig,
  params,
}: GetSequenceByIdServiceConfig): Promise<SequenceByIdResponse> {
  const client = await openAPIClient(httpRequestConfig);

  if (params === undefined) {
    throw new Error('Params are required');
  }

  const response = await client.GET('/v1/auth/sequence/{spaceId}/{id}/{sequenceLanguage}', {
    params: {
      path: {
        spaceId: httpRequestConfig.space,
        id: params.params.id,
        sequenceLanguage: params.params.sequenceLanguage,
      },
      query: params.query,
    },
  });

  if (response.error !== undefined) {
    throw new Error(response.error.message);
  }

  return response.data;
}

export async function createSequenceService({
  httpRequestConfig,
  params,
}: CreateSequenceServiceConfig): Promise<SequenceCreateResponse> {
  const client = await openAPIClient(httpRequestConfig);

  if (params === undefined) {
    throw new Error('Params are required');
  }

  const response = await client.POST('/v1/auth/sequence/{spaceId}/{sequenceLanguage}', {
    params: {
      path: {
        spaceId: httpRequestConfig.space,
        sequenceLanguage: params.params.sequenceLanguage,
      },
    },
    body: params.body,
  });

  if (response.error !== undefined) {
    throw new Error(response.error.message);
  }

  return response.data;
}

export async function updateSequenceService({
  httpRequestConfig,
  params,
}: UpdateSequenceServiceConfig): Promise<SequenceUpdateResponse> {
  const client = await openAPIClient(httpRequestConfig);

  if (params === undefined) {
    throw new Error('Params are required');
  }

  const response = await client.PUT('/v1/auth/sequence/{spaceId}/{id}/{sequenceLanguage}', {
    params: {
      path: {
        spaceId: httpRequestConfig.space,
        id: params.params.id,
        sequenceLanguage: params.params.sequenceLanguage,
      },
      query: params.query,
    },
    body: params.body,
  });

  if (response.error !== undefined) {
    throw new Error(response.error.message);
  }

  return response.data;
}

export async function deleteSequenceService({
  httpRequestConfig,
  params,
}: DeleteSequenceServiceConfig): Promise<void> {
  const client = await openAPIClient(httpRequestConfig);

  if (params === undefined) {
    throw new Error('Params are required');
  }

  const response = await client.DELETE('/v1/auth/sequence/{spaceId}/{id}', {
    params: {
      path: {
        spaceId: httpRequestConfig.space,
        id: params.params.id,
      },
    },
  });

  if (response.error !== undefined) {
    throw new Error(response.error.message);
  }
}

export async function translateSequenceService({
  httpRequestConfig,
  params,
}: TranslateSequenceServiceConfig): Promise<SequenceTranslateResponse> {
  const client = await openAPIClient(httpRequestConfig);

  if (params === undefined) {
    throw new Error('Params are required');
  }

  const response = await client.POST('/v1/auth/sequenceActions/legacyTranslateSequence/{spaceId}/{id}', {
    params: {
      path: {
        spaceId: httpRequestConfig.space,
        id: params.params.id,
      },
    },
    body: params.body,
  });

  if (response.error !== undefined) {
    throw new Error(response.error.message);
  }

  return response.data;
}

export async function synthesizeSpeechService({
  httpRequestConfig,
  params,
}: SynthesizeSpeechServiceConfig): Promise<SequenceSynthesizeSpeechResponse> {
  const client = await openAPIClient(httpRequestConfig);

  if (params === undefined) {
    throw new Error('Params are required');
  }

  const response = await client.POST('/v1/auth/sequenceActions/synthesizeSpeech/{spaceId}', {
    params: {
      path: {
        spaceId: httpRequestConfig.space,
      },
    },
    body: params.body,
  });

  if (response.error !== undefined) {
    throw new Error(response.error.message);
  }

  return response.data;
}

export async function instructionFeedbackService({
  httpRequestConfig,
  params,
}: InstructionFeedbackServiceConfig): Promise<SequenceInstructionFeedbackResponse> {
  const client = await openAPIClient(httpRequestConfig);

  if (params === undefined) {
    throw new Error('Params are required');
  }

  const response = await client.POST('/v1/auth/sequenceActions/instructionFeedback/{spaceId}', {
    params: {
      path: {
        spaceId: httpRequestConfig.space,
      },
    },
    body: params.body,
  });

  if (response.error !== undefined) {
    throw new Error(response.error.message);
  }

  return response.data;
}

export async function getSequenceChangelogService({
  httpRequestConfig,
  params,
}: SequenceChangelogServiceConfig): Promise<SequenceChangelogResponse> {
  const client = await openAPIClient(httpRequestConfig);

  if (params === undefined) {
    throw new Error('Params are required');
  }

  const response = await client.GET('/v1/auth/sequence/{spaceId}/{id}/{sequenceLanguage}/changelog', {
    params: {
      path: {
        spaceId: httpRequestConfig.space,
        id: params.params.id,
        sequenceLanguage: params.params.sequenceLanguage,
      },
      query: {
        language: params.query?.language,
      },
    },
  });

  if (response.error !== undefined) {
    throw new Error(response.error.message);
  }

  return response.data;
}

export async function patchSequenceService({
  httpRequestConfig,
  params,
}: PatchSequenceServiceConfig): Promise<SequencePatchResponse> {
  const client = await openAPIClient(httpRequestConfig);

  if (params === undefined) {
    throw new Error('Params are required');
  }

  const response = await client.PATCH('/v1/auth/sequence/{spaceId}/{id}/{sequenceLanguage}', {
    params: {
      path: {
        spaceId: httpRequestConfig.space,
        id: params.params.id,
        sequenceLanguage: params.params.sequenceLanguage,
      },
      query: params.query,
    },
    body: params.body,
  });

  if (response.error !== undefined) {
    throw new Error(response.error.message);
  }

  return response.data;
}
